import { Component, input, OnInit, OnDestroy, Renderer2, inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ProjectContextService } from '../../../../services/project-context.service';

@Component({
  selector: 'app-info-icon',
  standalone: true,
  imports: [CommonModule],
  template: `
    <span class="inline-flex items-center">
      <button
        type="button"
        (click)="togglePopover($event)"
        class="inline-flex items-center justify-center w-4 h-4 rounded-full border border-slate-300 text-slate-400 hover:text-secondary hover:border-secondary transition-colors text-[10px] font-bold leading-none flex-shrink-0 cursor-pointer"
        [attr.aria-label]="'More information about ' + heading()"
      >
        ?
      </button>
    </span>
  `,
  styles: [
    `
      :host {
        display: inline-flex;
      }
    `,
  ],
})
export class InfoIconComponent implements OnInit, OnDestroy {
  readonly heading = input.required<string>();
  readonly content = input.required<string>();
  readonly guideAnchor = input<string>();

  private renderer = inject(Renderer2);
  private document = inject(DOCUMENT);
  private ctx = inject(ProjectContextService);

  private popoverEl: HTMLElement | null = null;

  ngOnInit(): void {
    this.renderer.listen(this.document, 'click', () => this.closePopover());
    this.renderer.listen(this.document, 'keydown.escape', () => this.closePopover());
  }

  ngOnDestroy(): void {
    this.removePopover();
  }

  togglePopover(event: MouseEvent): void {
    event.stopPropagation();
    if (this.popoverEl) {
      this.closePopover();
      return;
    }
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const popoverWidth = 288;
    let x = rect.left + rect.width / 2 - popoverWidth / 2;
    if (x < 8) x = 8;
    if (x + popoverWidth > window.innerWidth - 8) {
      x = window.innerWidth - popoverWidth - 8;
    }
    const y = rect.bottom + 8;

    this.popoverEl = this.renderer.createElement('div');
    this.renderer.addClass(this.popoverEl, 'fixed');
    this.renderer.setStyle(this.popoverEl, 'z-index', '9999');
    this.renderer.setStyle(this.popoverEl, 'width', '288px');
    this.renderer.setStyle(this.popoverEl, 'background', '#fff');
    this.renderer.setStyle(this.popoverEl, 'border-radius', '12px');
    this.renderer.setStyle(
      this.popoverEl,
      'box-shadow',
      '0 4px 16px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.06)',
    );
    this.renderer.setStyle(this.popoverEl, 'border', '1px solid rgba(226, 232, 240, 0.9)');
    this.renderer.setStyle(this.popoverEl, 'padding', '16px');
    this.renderer.setStyle(this.popoverEl, 'left', x + 'px');
    this.renderer.setStyle(this.popoverEl, 'top', y + 'px');
    this.renderer.setStyle(
      this.popoverEl,
      'animation',
      'tooltip-enter 0.15s cubic-bezier(0.4,0,0.2,1)',
    );

    const headingEl = this.renderer.createElement('h4');
    this.renderer.addClass(headingEl, 'text-sm');
    this.renderer.setStyle(headingEl, 'font-weight', '700');
    this.renderer.setStyle(headingEl, 'color', '#111827');
    this.renderer.setStyle(headingEl, 'margin-bottom', '6px');
    this.renderer.setStyle(headingEl, 'padding-right', '16px');
    this.renderer.appendChild(headingEl, this.renderer.createText(this.heading()));
    this.renderer.appendChild(this.popoverEl, headingEl);

    const contentEl = this.renderer.createElement('p');
    this.renderer.setStyle(contentEl, 'font-size', '12px');
    this.renderer.setStyle(contentEl, 'color', '#4b5563');
    this.renderer.setStyle(contentEl, 'line-height', '1.625');
    this.renderer.setStyle(contentEl, 'margin-bottom', '8px');
    this.renderer.appendChild(contentEl, this.renderer.createText(this.content()));
    this.renderer.appendChild(this.popoverEl, contentEl);

    const anchor = this.guideAnchor();
    if (anchor) {
      const linkEl = this.renderer.createElement('button');
      this.renderer.setStyle(linkEl, 'font-size', '12px');
      this.renderer.setStyle(linkEl, 'font-weight', '600');
      this.renderer.setStyle(linkEl, 'color', '#1528cc');
      this.renderer.setStyle(linkEl, 'background', 'none');
      this.renderer.setStyle(linkEl, 'border', 'none');
      this.renderer.setStyle(linkEl, 'padding', '0');
      this.renderer.setStyle(linkEl, 'cursor', 'pointer');
      this.renderer.setProperty(linkEl, 'innerHTML', 'Learn more in the Project Guide &rarr;');
      this.renderer.listen(linkEl, 'click', (e: MouseEvent) => {
        e.stopPropagation();
        this.removePopover();
        this.ctx.requestGuide(anchor);
      });
      this.renderer.appendChild(this.popoverEl, linkEl);
    }

    this.renderer.listen(this.popoverEl, 'click', (e: MouseEvent) => {
      e.stopPropagation();
    });

    this.renderer.appendChild(this.document.body, this.popoverEl);
  }

  private closePopover(): void {
    this.removePopover();
  }

  private removePopover(): void {
    if (this.popoverEl) {
      this.popoverEl.remove();
      this.popoverEl = null;
    }
  }
}
